import getLocaleFromURL, {
  getLocaleFromPathname,
  getLocaleFromAkamai,
  getLocaleFromOneTrust,
  getStateFromAkamai,
  getStateFromOneTrust,
} from "./locale";
import { logger, AnalyticsModuleBootstrapError } from "../debug";

const STAGING_HOSTNAME_PATTERNS: ReadonlyArray<RegExp> = [
  /^(?:[a-z0-9-]+\.)+nonprod-lululemon\.com$/i,
  /^(?:stage|preview)\.lululemon\.(?:com|[a-z]{2}|(?:com|co)\.[a-z]{2}|demandware\.net)$/i,
  /^(?:[a-z0-9-]+\.)*sandbox\.eshopworld\.com$/i,
];

export const ESW_PATHNAME = "eshopworld";
const APAC_CODES = ["au", "nz", "jp", "kr", "hk"] as const;
const EMEA_CODES = ["eu", "de", "fr", "gb", "es", "lu"] as const;
const NA_CODES = ["us", "ca", "mx"] as const;
// will expand post test
const ESW_EU_REGIONS = [
  "at",
  "be",
  "ch",
  "cz",
  "dk",
  "fi",
  "ie",
  "it",
  "nl",
  "no",
  "pl",
  "se",
  "hr",
  "ee",
  "gr",
  "hu",
  "lu",
  "lv",
  "li",
  "lt",
  "mc",
  "ro",
  "sk",
  "si",
] as const;

//all APAC regions for ESW fall under HK
const ESW_APAC_REGIONS = ["id", "in", "my", "ph", "sg", "th", "tw", "vn"] as const;

const ESW_NAM_REGIONS = ["mx"] as const;

const ESW_REGIONS = [
  ...ESW_EU_REGIONS,
  ...ESW_APAC_REGIONS,
  ...ESW_NAM_REGIONS,
];

export function mapLocaleToAnalyticsRegion(locale: string): string {
  if (ESW_EU_REGIONS.includes(locale as (typeof ESW_EU_REGIONS)[number])) {
    return "eu";
  }
  if (ESW_APAC_REGIONS.includes(locale as (typeof ESW_APAC_REGIONS)[number])) {
    return "hk";
  }
  return locale;
}

function getValidRegion(url: URL): string {
  const locale = getLocaleFromURL(url);
  return mapLocaleToAnalyticsRegion(locale);
}

export function getRegion(): string {
  try {
    if (typeof window === "undefined") {
      return "";
    }
    const url = new URL(window.location.href);
    return getValidRegion(url);
  } catch (err) {
    logger.error(
      new AnalyticsModuleBootstrapError("Failed to get region", { cause: err })
    );
  }
  return "";
}

export function getSpecificRegion(): string {
  try {
    if (typeof window === "undefined") {
      return "";
    }
    return getLocaleFromPathname(window.location.pathname) ?? "us";
  } catch (err) {
    logger.error(
      new AnalyticsModuleBootstrapError("Failed to get specific region", {
        cause: err,
      })
    );
  }
  return "";
}

export function isESWRegion(locale: string): boolean {
  return ESW_REGIONS.includes(locale as (typeof ESW_REGIONS)[number]);
}

export function isStaging(): boolean {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    const url = new URL(window.location.href);
    return STAGING_HOSTNAME_PATTERNS.some((pattern) =>
      pattern.test(url.hostname)
    );
  } catch {
    return false;
  }
}

export function isAPAC(): boolean {
  const region = getRegion();
  return APAC_CODES.includes(region as (typeof APAC_CODES)[number]);
}

export function isEMEA(): boolean {
  const region = getRegion();
  return EMEA_CODES.includes(region as (typeof EMEA_CODES)[number]);
}

export function isNA(): boolean {
  const region = getRegion();
  return NA_CODES.includes(region as (typeof NA_CODES)[number]);
}

export function isESW(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.pathname.includes(ESW_PATHNAME);
}

export function isQuebec(): boolean {
  const region =
    getLocaleFromAkamai() ?? getLocaleFromOneTrust() ?? getRegion();
  const state = getStateFromAkamai() ?? getStateFromOneTrust();
  // Either we could detect QC or we're in CA but couldn't detect the state
  // QC is the only state in CA with different privacy laws
  // so we take the safe bet.
  return region === "ca" && (state === "qc" || state === null);
}
